import { track } from "./tracker";
import { trackActivationCompleted } from "./onboarding-flow";
import { canonicalProductType } from "./product-type-canonical";
import { trackConversion } from "./trackConversion";
import { readSourceTool } from "./tool-events";
import { ANALYTICS_EVENTS } from "../../../shared/analytics/events";

export function trackContactFormView(formId: string): void {
  track("lead.form_viewed", { source: "contact", formId });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_FORM_VIEWED);
}

export function trackContactFormSubmitSuccess(data: {
  formId: string;
  inquiryType: string;
}): void {
  track("lead.form_submitted", {
    source: "contact",
    source_tool: readSourceTool(),
    formId: data.formId,
    inquiryType: data.inquiryType,
  });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_FORM_SUBMITTED);
}

export interface RfqFormViewEvent {
  formId: string;
  listingId?: string;
  productTypeSlug?: string;
  productTypeName?: string;
  serviceType?: string;
  mode?: "anonymous" | "authenticated";
  embedded?: boolean;
  prefilledFields?: readonly string[];
}

export function trackRfqFormView(data: RfqFormViewEvent): void {
  track("lead.form_viewed", {
    source: "rfq",
    formId: data.formId,
    listingId: data.listingId,
    productType: canonicalProductType(
      data.productTypeSlug,
      data.productTypeName,
    ),
    productTypeSlug: data.productTypeSlug,
    productTypeName: data.productTypeName,
    serviceType: data.serviceType,
    mode: data.mode,
    embedded: data.embedded,
    ...(data.prefilledFields?.length
      ? { prefilledFields: [...data.prefilledFields] }
      : {}),
  });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_FORM_VIEWED);
}

export interface RfqFormSubmitEvent {
  formId: string;
  rfqNumber: string;
  leadId?: string;
  listingId?: string;
  productTypeSlug?: string;
  productTypeName?: string;
  serviceType?: string;
  quantity?: number;
  leadQuality?: string;
  estimatedValue?: number;
  mode?: "anonymous" | "authenticated";
}

export function trackRfqFormSubmitSuccess(data: RfqFormSubmitEvent): void {
  track("lead.form_submitted", {
    source: "rfq",
    source_tool: readSourceTool(),
    formId: data.formId,
    leadNumber: data.rfqNumber,
    lead_id: data.leadId,
    listingId: data.listingId,
    productType: canonicalProductType(
      data.productTypeSlug,
      data.productTypeName,
    ),
    productTypeSlug: data.productTypeSlug,
    productTypeName: data.productTypeName,
    serviceType: data.serviceType,
    quantity: data.quantity,
    lead_quality: data.leadQuality,
    value: data.estimatedValue,
    currency: "USD",
    mode: data.mode,
  });
  trackConversion("lead_submitted", {
    entityId: data.leadId,
    value: data.estimatedValue,
    qualityTier: data.leadQuality,
    productTypeSlug: data.productTypeSlug,
    productTypeName: data.productTypeName,
    quantity: data.quantity,
    leadType: data.serviceType,
    surface: data.formId,
    listingId: data.listingId,
  });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_FORM_SUBMITTED);
}

export interface RfqConversionEvent {
  formId: string;
  rfqNumber?: string;
  leadId?: string;
  listingId?: string;
  email?: string;
  emailProvided?: boolean;
  productTypeSlug?: string;
  productTypeName?: string;
  serviceType?: string;
  quantity?: string;
  leadQuality?: string;
  estimatedValue?: number;
}

export function trackRfqConversion(data: RfqConversionEvent): void {
  track("lead.thank_you", {
    source: "rfq",
    formId: data.formId,
    leadNumber: data.rfqNumber,
    lead_id: data.leadId,
    listingId: data.listingId,
    productType: canonicalProductType(
      data.productTypeSlug,
      data.productTypeName,
    ),
    productTypeSlug: data.productTypeSlug,
    serviceType: data.serviceType,
    quantity: data.quantity,
    lead_quality: data.leadQuality,
    value: data.estimatedValue,
    currency: "USD",
    emailProvided: data.emailProvided ?? !!data.email,
  });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_THANK_YOU);
}

export interface RfqFormBlockedEvent {
  formId: string;
  step?: string;
  wizardStep?: string;
  blockedByField?: string;
  errorCount?: number;
}

export function trackRfqFormBlocked(data: RfqFormBlockedEvent): void {
  const wizardStep = data.wizardStep ?? data.step;
  track("lead.form_blocked", {
    source: "rfq",
    formId: data.formId,
    step: data.step,
    wizardStep,
    blockedByField: data.blockedByField,
    errorCount: data.errorCount,
  });
}

export interface ListingQuoteFormViewEvent {
  formId: string;
  listingId: string;
  productTypeSlug?: string;
  productTypeName?: string;
}

export function trackListingQuoteFormView(
  data: ListingQuoteFormViewEvent,
): void {
  track("lead.form_viewed", {
    source: "listing_quote",
    formId: data.formId,
    listingId: data.listingId,
    productTypeSlug: data.productTypeSlug,
    productTypeName: data.productTypeName,
  });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_FORM_VIEWED);
}

export interface ListingQuoteSubmitEvent {
  city?: string;
  state?: string;
  formId: string;
  listingId: string;
  leadNumber: string;
  leadId?: string;
  productTypeSlug?: string;
  productTypeName?: string;
  quantity?: number;
  leadQuality?: string;
  estimatedValue?: number;
}

export function trackListingQuoteSubmitSuccess(
  data: ListingQuoteSubmitEvent,
): void {
  track("lead.form_submitted", {
    source: "listing_quote",
    source_tool: readSourceTool(),
    city: data.city,
    state: data.state,
    formId: data.formId,
    listingId: data.listingId,
    leadNumber: data.leadNumber,
    lead_id: data.leadId,
    productTypeSlug: data.productTypeSlug,
    productTypeName: data.productTypeName,
    quantity: data.quantity,
    lead_quality: data.leadQuality,
    value: data.estimatedValue,
    currency: "USD",
  });
  trackConversion("lead_submitted", {
    entityId: data.leadId,
    value: data.estimatedValue,
    qualityTier: data.leadQuality,
    productTypeSlug: data.productTypeSlug,
    productTypeName: data.productTypeName,
    quantity: data.quantity,
    leadType: "buy",
    surface: data.formId,
    listingId: data.listingId,
  });
  trackActivationCompleted(ANALYTICS_EVENTS.LEAD_FORM_SUBMITTED);
}

export function trackDraftRecovery(data: {
  draftAgeMs: number;
  recoveredStep: string;
  completedStepsCount: number;
  formType: "rfq_wizard" | "quote_checkout";
}): void {
  track("lead.draft_recovered", {
    draftAgeMs: data.draftAgeMs,
    draftAgeHours: Math.round((data.draftAgeMs / (1000 * 60 * 60)) * 10) / 10,
    recoveredStep: data.recoveredStep,
    completedStepsCount: data.completedStepsCount,
    formType: data.formType,
  });
}
